import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Öffnet ein authentifiziertes PDF im globalen PDF-Viewer-Modal
 * (siehe PdfViewerRoot in App.js). Fallback: falls das Modal nicht
 * eingebunden ist (z.B. auf public/embed-Seiten), Blob-URL + neuer Tab.
 *
 * @param {string} path z.B. "/tickets/xxx/pdf"
 * @param {string} [filename] Anzeigename für die Toolbar
 */
export function openPdf(path, filename) {
  // Modal via CustomEvent aufrufen — hört PdfViewerRoot im App.js
  const detail = { path, filename: filename || path.split("/").pop() };
  const dispatched = window.dispatchEvent(new CustomEvent("mh:open-pdf", { detail }));
  // Wenn kein Listener aktiv: als Blob im neuen Tab öffnen (defensiver Fallback)
  if (!window.__mhPdfModalMounted) return openPdfInNewTab(path);
  return dispatched;
}

/**
 * Direkter Fallback: PDF via Blob-URL in neuem Tab.
 */
export async function openPdfInNewTab(path) {
  try {
    const { data } = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    const win = window.open(url, "_blank");
    if (!win) toast.error("Pop-up blockiert – bitte Pop-ups erlauben");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return url;
  } catch {
    toast.error("PDF konnte nicht geladen werden – bitte neu einloggen");
    return null;
  }
}

/**
 * Lädt ein authentifiziertes PDF als Datei herunter.
 */
export async function downloadPdf(path, filename = "document.pdf") {
  try {
    const { data } = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch {
    toast.error("PDF-Download fehlgeschlagen");
  }
}

/**
 * Fetcht ein authentifiziertes PDF als Blob-URL — für iframe src ohne 401.
 * Ruf `URL.revokeObjectURL(url)` beim Cleanup auf.
 */
export async function fetchPdfBlobUrl(path) {
  const { data } = await api.get(path, { responseType: "blob" });
  return URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
}
